import React, { useCallback, useEffect, useMemo, useState } from "react"
import ContainerBox from "../components/ui/containerBox"
import MastersList from "../components/master/mastersList"
import PageSizeControl from "../components/ui/pageSizeControl"
import dictionary from "../utils/dictionary"
import { useSelector } from "react-redux"
import userService from "../services/user.service"
import { isRequestCanceled } from "../utils/isRequestCanceled"

const MASTERS_PAGE_SIZE_DEFAULT = 50

const MastersPage = () => {
  const selectedLanguage = useSelector((state) => state.lang.lang)
  const [pageSize, setPageSize] = useState(MASTERS_PAGE_SIZE_DEFAULT)
  const [staffUsers, setStaffUsers] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setStaffUsers([])
    setOffset(0)

    userService
      .getUsers({ limit: pageSize, offset: 0, signal: controller.signal })
      .then((page) => {
        const staff = (page ?? []).filter((user) => user.isStaff)
        setStaffUsers(staff)
        setOffset(page?.length ?? 0)
        setHasMore((page?.length ?? 0) >= pageSize)
      })
      .catch((error) => {
        if (!isRequestCanceled(error)) {
          console.error(error)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })

    return () => controller.abort()
  }, [pageSize])

  const loadMore = useCallback(async () => {
    const controller = new AbortController()
    try {
      const page = await userService.getUsers({
        limit: pageSize,
        offset,
        signal: controller.signal,
      })
      const staff = (page ?? []).filter((user) => user.isStaff)
      if (staff.length) {
        setStaffUsers((prev) => [...prev, ...staff])
        setOffset((prev) => prev + (page?.length ?? 0))
        setHasMore((page?.length ?? 0) >= pageSize)
      } else {
        setHasMore(false)
      }
    } catch (error) {
      if (!isRequestCanceled(error)) {
        console.error(error)
      }
    }
  }, [pageSize, offset])

  const virtualItems = useMemo(
    () =>
      staffUsers.map((user, index) => ({
        key: user.id,
        user,
        lastEl: index === staffUsers.length - 1,
      })),
    [staffUsers]
  )

  return (
    <div className="container-fluid relative mx-auto h-[calc(100vh-252px)] flex justify-center items-start bg-cream  max-md:text-xs">
      <ContainerBox>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-brown text-2xl max-md:text-lg">
            {dictionary[selectedLanguage].masters}
          </h2>
          <PageSizeControl value={pageSize} onChange={setPageSize} />
        </div>
        {loading ? (
          <p className="text-gray text-sm py-4">…</p>
        ) : (
          <MastersList
            items={virtualItems}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        )}
      </ContainerBox>
    </div>
  )
}

export default MastersPage
